/**
 * Free-tier masking (spec section 6.2).
 *
 * Two rules from the spec drive this module:
 *   1. every *non-space* character is replaced by `░`, spaces are preserved, so
 *      the word shape stays guessable;
 *   2. the masked length is close to — but deliberately NOT equal to — the real
 *      length, so the mask does not leak the exact character count.
 *
 * Rule 2 only has teeth if the mask is *stable*: if the fake length changed on
 * every render, a user could average the renders and recover the true length.
 * Hence the seeded PRNG: the same (value, seed) pair always produces the same
 * mask, but two fields with the same length produce different masks.
 *
 * Masking itself happens on the server (closed data is never sent to the
 * browser); this module exists for the values the client legitimately holds —
 * e.g. the public company page masks personal names it is allowed to receive in
 * masked form, and the demo/preview states of the card.
 */

export const MASK_CHAR = '░';

/** FNV-1a, 32-bit. Small, dependency-free and stable across engines. */
function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 — deterministic, seeded 32-bit PRNG. */
function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Jitter for a run of `length` non-space characters: ±1 for runs of two or
 * more, and never zero. A single character becomes two `░` because "one or two"
 * is already non-committal, while rendering exactly one would leak the length.
 */
function maskedRunLength(length: number, random: () => number): number {
  if (length <= 1) return 2;
  return random() < 0.5 ? length - 1 : length + 1;
}

/**
 * Replace a value with `░`, keeping spaces and punctuation-independent shape.
 *
 * @param value the real value; never rendered, only measured
 * @param seed  stable identifier (e.g. `${blockKey}:${field}`) so the fake
 *              length never changes between renders
 */
export function maskValue(value: string, seed: string): string {
  const random = createRandom(hashString(seed));
  let out = '';
  let runStart = -1;

  const flushRun = (endExclusive: number) => {
    if (runStart < 0) return;
    out += MASK_CHAR.repeat(maskedRunLength(endExclusive - runStart, random));
    runStart = -1;
  };

  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (char === ' ' || char === '\t' || char === '\n' || char === '\u00a0') {
      flushRun(i);
      out += char === '\u00a0' ? ' ' : char;
    } else if (runStart < 0) {
      runStart = i;
    }
  }
  flushRun(value.length);

  return out;
}

/**
 * Mask a value that may be absent. Returns a fixed-width `░` placeholder when
 * there is nothing to measure, so a masked block never collapses to an empty
 * line that reads as "this company has no founders".
 */
export function maskUnknown(seed: string, width = 8): string {
  const random = createRandom(hashString(seed));
  return MASK_CHAR.repeat(Math.max(2, Math.round(width * (0.7 + random() * 0.6))));
}

/**
 * True when the string is already a mask produced by the server (the public
 * company page receives `nameMasked` ready-made). Such strings are rendered
 * verbatim: masking them again would only change the shape the server chose.
 */
export function isAlreadyMasked(value: string): boolean {
  return value.includes(MASK_CHAR);
}
